import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  MessageCircle, CreditCard, Wallet, Settings, CheckCircle2, XCircle,
  RefreshCw, Copy, ShoppingCart, Facebook,
  Zap, Shield, Key, Phone, Building2, Eye, EyeOff, Send, MessageSquare
} from "lucide-react";

const FB_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || "";
const FB_CONFIG_ID = process.env.REACT_APP_FACEBOOK_CONFIG_ID || "";

// ── Safe error message extractor ─────────────────────────────────────────────
// Pydantic 422 errors return detail as an array of objects — never pass that
// directly to toast.error() or React will crash trying to render an object.
function extractErrorMessage(err) {
  try {
    const detail = err && err.response && err.response.data && err.response.data.detail;
    if (!detail) return null;
    if (typeof detail === "string") return detail;
    // Pydantic validation error array: [{type, loc, msg, input, url}, ...]
    if (Array.isArray(detail)) {
      return detail
        .map(function (d) {
          var field = Array.isArray(d.loc) ? d.loc.join(" → ") : String(d.loc || "");
          return field ? field + ": " + d.msg : d.msg;
        })
        .join(" | ");
    }
    if (typeof detail === "object") return JSON.stringify(detail);
    return String(detail);
  } catch (e) {
    return null;
  }
}

export default function WhatsAppAccount() {
  const navigate = useNavigate();
  const { currentBranch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [credits, setCredits] = useState({ available: 0, used: 0, total: 0 });
  const [pricing, setPricing] = useState({ pricePerCredit: 0.5, currency: "INR" });

  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showTestResultModal, setShowTestResultModal] = useState(false);
  const [showSendTestModal, setShowSendTestModal] = useState(false);

  const [fbReady, setFbReady] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);

  const [manualForm, setManualForm] = useState({
    phoneNumberId: "",
    businessAccountId: "",
    accessToken: "",
    displayPhoneNumber: ""
  });
  const [showToken, setShowToken] = useState(false);

  const [testLoading, setTestLoading] = useState(false);
  const [testResults, setTestResults] = useState(null);

  const [testPhoneNumber, setTestPhoneNumber] = useState("");
  const [sendTestLoading, setSendTestLoading] = useState(false);
  const [testMessageResult, setTestMessageResult] = useState(null);

  const [creditAmount, setCreditAmount] = useState(100);
  const [buyLoading, setBuyLoading] = useState(false);

  const [configForm, setConfigForm] = useState({
    webhookUrl: "",
    webhookVerifyToken: ""
  });

  // ─── Facebook SDK init ──────────────────────────────────────────────────
  useEffect(() => {
    if (!window.FB && FB_APP_ID) {
      window.fbAsyncInit = function () {
        window.FB.init({ appId: FB_APP_ID, cookie: true, xfbml: true, version: "v25.0" });
        setFbReady(true);
      };
      (function (d, s, id) {
        var js, fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        js = d.createElement(s);
        js.id = id;
        js.src = "https://connect.facebook.net/en_US/sdk.js";
        js.async = true;
        js.defer = true;
        fjs.parentNode.insertBefore(js, fjs);
      }(document, "script", "facebook-jssdk"));
    } else if (window.FB) {
      setFbReady(true);
    }
  }, []);

  // ─── Embedded Signup postMessage listener ────────────────────────────────
  const sessionInfoListener = useCallback((event) => {
    console.log("[sessionInfoListener] Raw event origin:", event.origin);
    
    // Accept from any Meta/Facebook subdomain
    if (!event.origin.includes("facebook.com") && !event.origin.includes("meta.com")) return;

    try {
      // Handle both string and object data from Meta
      var data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      console.log("[sessionInfoListener] Parsed data:", JSON.stringify(data));

      if (data.type === "WA_EMBEDDED_SIGNUP") {
        console.log("[WA Embedded Signup postMessage]", data.event, data.data || "");
        if (data.event === "FINISH") {
          window._waEmbeddedData = {
            phone_number_id: data.data.phone_number_id,
            waba_id: data.data.waba_id
          };
        } else if (data.event === "CANCEL") {
          setConnectLoading(false);
          toast.error("WhatsApp signup was cancelled");
        } else if (data.event === "ERROR") {
          toast.error("WhatsApp signup error occurred");
          setConnectLoading(false);
        }
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    window.addEventListener("message", sessionInfoListener);
    return () => window.removeEventListener("message", sessionInfoListener);
  }, [sessionInfoListener]);

  useEffect(() => { fetchAccountData(); }, []);

  // ─── Data fetch ───────────────────────────────────────────────────────────
  const fetchAccountData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/whatsapp/account");
      setAccount(res.data.account);
      setCredits(res.data.credits);
      setPricing(res.data.pricing);
      if (res.data.account) {
        setConfigForm({
          webhookUrl: res.data.account.webhookUrl || "",
          webhookVerifyToken: res.data.account.webhookVerifyToken || ""
        });
      }
    } catch (err) {
      console.error("Failed to fetch WhatsApp account:", err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Sync onClick wrappers ────────────────────────────────────────────────
  const onClickTestConnection  = () => { handleTestConnection(); };
  const onClickManualConnect   = () => { handleManualConnect(); };
  const onClickDisconnect      = () => { handleDisconnect(); };
  const onClickBuyCredits      = () => { handleBuyCredits(); };
  const onClickSaveConfig      = () => { handleSaveConfig(); };
  const onClickSendTestMessage = () => { handleSendTestMessage(); };

  // ─── Facebook Embedded Signup ─────────────────────────────────────────────
  const handleFacebookSignup = () => {
    if (!window.FB) {
      toast.error("Facebook SDK not loaded. Please refresh the page.");
      return;
    }
    if (!FB_APP_ID || !FB_CONFIG_ID) {
      toast.error("Facebook App configuration is missing");
      return;
    }

    window._waEmbeddedData = null;
    setConnectLoading(true);

    const redirectUri = "https://kuppu.optimusbytes.com/";

    window.FB.login(
      function (response) {
        console.log("[FB.login] full response:", JSON.stringify(response));

        if (!response.authResponse) {
          console.error("[FB.login] No authResponse found");
          toast.error("Facebook login was cancelled or failed");
          setConnectLoading(false);
          return;
        }

        // The JS SDK returns accessToken directly if configured for 'Access token' in the portal
        var token = response.authResponse.accessToken;
        var code = response.authResponse.code;
        
        console.log("[FB.login] Received credentials:", { 
          hasToken: !!token, 
          hasCode: !!code,
          status: response.status 
        });

        if (!token && !code) {
          toast.error("No authorization credentials received from Facebook");
          setConnectLoading(false);
          return;
        }

        // Capture the exact WABA and Phone ID from the signup window if available
        const embeddedData = window._waEmbeddedData || {};

        const payload = { 
          access_token: token || "",
          code: code || "",
          phone_number_id: embeddedData.phone_number_id || "",
          waba_id: embeddedData.waba_id || ""
        };

        api.post("/whatsapp/account/connect-oauth", payload)
          .then(function (res) {
            toast.success("WhatsApp Business Account connected successfully!");
            setShowConnectModal(false);
            fetchAccountData();
          })
          .catch(function (err) {
            var msg = extractErrorMessage(err) || "Failed to connect WhatsApp account.";
            toast.error(msg);
          })
          .finally(function () {
            setConnectLoading(false);
          });
      },
      {
        config_id: FB_CONFIG_ID,
        extras: {
          setup: {},
          featureType: "",
          sessionInfoVersion: 2
        }
      }
    );
  };

  // ─── Other async handlers ────────────────────────────────────────────────
  const handleManualConnect = async () => {
    if (!manualForm.phoneNumberId || !manualForm.businessAccountId || !manualForm.accessToken) {
      toast.error("Please fill in all required fields");
      return;
    }
    setConnectLoading(true);
    try {
      const res = await api.post("/whatsapp/account/connect-manual", manualForm);
      toast.success("WhatsApp Business Account connected successfully!");
      setShowConnectModal(false);
      fetchAccountData();
      setManualForm({ phoneNumberId: "", businessAccountId: "", accessToken: "", displayPhoneNumber: "" });
      if (res.data.webhookVerifyToken) {
        toast.info("Webhook Verify Token: " + res.data.webhookVerifyToken, { duration: 10000 });
      }
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to connect. Please verify your credentials.");
    } finally {
      setConnectLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect WhatsApp? You won't be able to send messages until you reconnect.")) return;
    try {
      await api.post("/whatsapp/account/disconnect");
      toast.success("WhatsApp account disconnected");
      fetchAccountData();
    } catch (err) {
      toast.error("Failed to disconnect");
    }
  };

  const handleTestConnection = async () => {
    setTestLoading(true);
    setTestResults(null);
    try {
      const res = await api.post("/whatsapp/account/test-connection");
      setTestResults(res.data);
      setShowTestResultModal(true);
      if (res.data.success) {
        toast.success("Connection test successful!");
        fetchAccountData();
      } else {
        toast.error("Connection test failed. Check details for more info.");
      }
    } catch (err) {
      const errorMsg = extractErrorMessage(err) || "Failed to test connection";
      toast.error(errorMsg);
      setTestResults({ success: false, message: errorMsg, results: { errors: [errorMsg] } });
      setShowTestResultModal(true);
    } finally {
      setTestLoading(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testPhoneNumber || testPhoneNumber.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setSendTestLoading(true);
    setTestMessageResult(null);
    try {
      const res = await api.post("/whatsapp/account/send-test", { phoneNumber: testPhoneNumber });
      setTestMessageResult(res.data);
      toast.success("Test message sent successfully!");
    } catch (err) {
      const errorMsg = extractErrorMessage(err) || "Failed to send test message";
      toast.error(errorMsg);
      setTestMessageResult({ success: false, message: errorMsg });
    } finally {
      setSendTestLoading(false);
    }
  };

  const handleGoToChat = () => {
    if (testMessageResult && testMessageResult.memberId) {
      setShowSendTestModal(false);
      navigate("/whatsapp/messages?memberId=" + testMessageResult.memberId);
    }
  };

  const handleBuyCredits = async () => {
    if (creditAmount < 10) { toast.error("Minimum purchase is 10 credits"); return; }
    setBuyLoading(true);
    try {
      const orderRes = await api.post("/whatsapp/credits/create-order", { creditAmount });
      const { orderId, amount, keyId } = orderRes.data;
      const options = {
        key: keyId,
        amount,
        currency: "INR",
        name: "WhatsApp Credits",
        description: "Purchase " + creditAmount + " message credits",
        order_id: orderId,
        handler: function (response) {
          api.post("/whatsapp/credits/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            creditAmount
          })
            .then(function () {
              toast.success(creditAmount + " credits added successfully!");
              setShowBuyModal(false);
              fetchAccountData();
            })
            .catch(function () { toast.error("Payment verification failed"); });
        },
        prefill: {},
        theme: { color: "#dc2626" }
      };
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to create order");
    } finally {
      setBuyLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await api.put("/whatsapp/account/config", configForm);
      toast.success("Configuration saved");
      setShowConfigModal(false);
      fetchAccountData();
    } catch (err) {
      toast.error("Failed to save configuration");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // ─── Loading state ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const isConnected = account && account.isConnected;

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="whatsapp-account-page">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black font-heading tracking-tight uppercase">
            WhatsApp Account
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage your WhatsApp Business API connection and credits
          </p>
        </div>
      </div>

      {/* ── Connection Status Card ── */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-heading uppercase tracking-wide flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-green-500" />
            WhatsApp Business Cloud API
          </CardTitle>
          <Badge className={isConnected
            ? "bg-green-500/15 text-green-500 border-green-500/20"
            : "bg-zinc-500/15 text-zinc-400 border-zinc-500/20"
          }>
            {isConnected ? "Connected" : "Not Connected"}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-4">
          {isConnected ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-secondary/30 rounded-xl space-y-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Phone Number ID</p>
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-sm flex-1 truncate">{account.phoneNumberId}</p>
                    <Button variant="ghost" size="icon" className="h-6 w-6"
                      onClick={() => copyToClipboard(account.phoneNumberId)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-secondary/30 rounded-xl space-y-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">WABA ID</p>
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-sm flex-1 truncate">{account.businessAccountId}</p>
                    <Button variant="ghost" size="icon" className="h-6 w-6"
                      onClick={() => copyToClipboard(account.businessAccountId)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-secondary/30 rounded-xl space-y-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Display Number</p>
                  <p className="font-mono text-sm">{account.displayPhoneNumber || "N/A"}</p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-xl space-y-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Verified Name</p>
                  <p className="font-mono text-sm">{account.verifiedName || "N/A"}</p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-xl space-y-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Quality Rating</p>
                  <Badge variant="outline" className={"text-xs " + (
                    account.qualityRating === "GREEN" ? "text-emerald-500 border-emerald-500/20" :
                    account.qualityRating === "YELLOW" ? "text-amber-500 border-amber-500/20" :
                    account.qualityRating === "RED" ? "text-red-500 border-red-500/20" : ""
                  )}>
                    {account.qualityRating || "Unknown"}
                  </Badge>
                </div>
                <div className="p-4 bg-secondary/30 rounded-xl space-y-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Connection Method</p>
                  <Badge variant="outline" className="text-xs">
                    {account.connectionMethod === "oauth" ? "Embedded Signup" : "Manual"}
                  </Badge>
                </div>
              </div>

              <Separator />

              <div className="flex flex-wrap gap-2">
                <Button size="sm"
                  onClick={() => setShowSendTestModal(true)}
                  className="text-xs bg-blue-600 hover:bg-blue-700"
                  data-testid="send-test-msg-btn">
                  <Send className="h-4 w-4 mr-2" />Send Test Message
                </Button>
                <Button size="sm"
                  onClick={onClickTestConnection}
                  disabled={testLoading}
                  className="text-xs bg-emerald-600 hover:bg-emerald-700"
                  data-testid="test-connection-btn">
                  {testLoading
                    ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    : <Zap className="h-4 w-4 mr-2" />}
                  Test Connection
                </Button>
                <Button variant="outline" size="sm"
                  onClick={() => setShowConfigModal(true)}
                  className="text-xs"
                  data-testid="config-btn">
                  <Settings className="h-4 w-4 mr-2" />Webhook Config
                </Button>
                <Button variant="outline" size="sm"
                  onClick={onClickDisconnect}
                  className="text-xs text-destructive hover:text-destructive"
                  data-testid="disconnect-btn">
                  <XCircle className="h-4 w-4 mr-2" />Disconnect
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/10 flex items-center justify-center">
                <MessageCircle className="h-10 w-10 text-green-500" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">Connect WhatsApp Business</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto text-sm">
                Connect your WhatsApp Business Account to send messages, templates, and engage with your members.
              </p>
              <Button
                onClick={() => setShowConnectModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-6 text-base"
                data-testid="connect-whatsapp-btn">
                <MessageCircle className="h-5 w-5 mr-2" />Connect WhatsApp
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Credits Card ── */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-heading uppercase tracking-wide flex items-center gap-2">
            <Wallet className="h-5 w-5 text-amber-500" />
            Message Credits
          </CardTitle>
          <Button size="sm"
            onClick={() => setShowBuyModal(true)}
            className="text-xs font-heading uppercase"
            data-testid="buy-credits-btn">
            <ShoppingCart className="h-4 w-4 mr-2" />Buy Credits
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center">
              <p className="text-3xl font-black font-heading text-emerald-500">{credits.available}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Available</p>
            </div>
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl text-center">
              <p className="text-3xl font-black font-heading text-blue-500">{credits.used}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Used</p>
            </div>
            <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl text-center">
              <p className="text-3xl font-black font-heading text-purple-500">{credits.total}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Total Purchased</p>
            </div>
            <div className="p-4 bg-secondary/30 border border-border rounded-xl text-center">
              <p className="text-3xl font-black font-heading">₹{pricing.pricePerCredit}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Per Message</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Connect Modal ── */}
      <Dialog open={showConnectModal} onOpenChange={setShowConnectModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-500" />Connect WhatsApp Business
            </DialogTitle>
            <DialogDescription>Choose your preferred connection method below.</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="embedded" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="embedded" className="text-xs">
                <Facebook className="h-4 w-4 mr-2" />Embedded Signup
              </TabsTrigger>
              <TabsTrigger value="manual" className="text-xs">
                <Key className="h-4 w-4 mr-2" />Manual Setup
              </TabsTrigger>
            </TabsList>

            {/* Embedded Signup Tab */}
            <TabsContent value="embedded" className="space-y-4 mt-4">
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <p className="text-xs font-medium text-blue-500 mb-2">Recommended Method</p>
                <p className="text-xs text-muted-foreground">
                  Quick and secure connection using Meta's official Embedded Signup flow.
                </p>
              </div>
              <div className="space-y-3">
                <p className="text-xs font-medium">Prerequisites:</p>
                <ul className="text-xs text-muted-foreground space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Meta Business Portfolio account
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    A phone number not linked to any WhatsApp account
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    WhatsApp Business added to your Meta App
                  </li>
                </ul>
              </div>
              <Button
                onClick={handleFacebookSignup}
                disabled={!fbReady || connectLoading}
                className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold py-6"
                data-testid="connect-facebook-btn">
                {connectLoading
                  ? <><RefreshCw className="h-5 w-5 mr-2 animate-spin" />Connecting...</>
                  : <><Facebook className="h-5 w-5 mr-2" />Continue with Facebook</>}
              </Button>
              {!fbReady && (
                <p className="text-xs text-center text-muted-foreground">Loading Facebook SDK...</p>
              )}
            </TabsContent>

            {/* Manual Setup Tab */}
            <TabsContent value="manual" className="space-y-4 mt-4">
              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <p className="text-xs font-medium text-amber-500 mb-2">Advanced Users</p>
                <p className="text-xs text-muted-foreground">
                  For businesses with existing WhatsApp Cloud API setup.
                </p>
              </div>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5" />Phone Number ID *
                  </Label>
                  <Input
                    placeholder="e.g., 123456789012345"
                    value={manualForm.phoneNumberId}
                    onChange={(e) => setManualForm({ ...manualForm, phoneNumberId: e.target.value })}
                    data-testid="phone-number-id-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-2">
                    <Building2 className="h-3.5 w-3.5" />WhatsApp Business Account ID (WABA) *
                  </Label>
                  <Input
                    placeholder="e.g., 987654321098765"
                    value={manualForm.businessAccountId}
                    onChange={(e) => setManualForm({ ...manualForm, businessAccountId: e.target.value })}
                    data-testid="waba-id-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-2">
                    <Key className="h-3.5 w-3.5" />Permanent Access Token *
                  </Label>
                  <div className="relative">
                    <Input
                      type={showToken ? "text" : "password"}
                      placeholder="EAAxxxxxx..."
                      value={manualForm.accessToken}
                      onChange={(e) => setManualForm({ ...manualForm, accessToken: e.target.value })}
                      className="pr-10"
                      data-testid="access-token-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowToken(!showToken)}>
                      {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Get this from Meta Developer Console → Your App → WhatsApp → API Setup
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5" />Display Phone Number (optional)
                  </Label>
                  <Input
                    placeholder="e.g., +91 98765 43210"
                    value={manualForm.displayPhoneNumber}
                    onChange={(e) => setManualForm({ ...manualForm, displayPhoneNumber: e.target.value })}
                  />
                </div>
              </div>
              <Button
                onClick={onClickManualConnect}
                disabled={connectLoading}
                className="w-full py-6"
                data-testid="connect-manual-btn">
                {connectLoading
                  ? <><RefreshCw className="h-5 w-5 mr-2 animate-spin" />Validating &amp; Connecting...</>
                  : <><Shield className="h-5 w-5 mr-2" />Validate &amp; Connect</>}
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* ── Buy Credits Modal ── */}
      <Dialog open={showBuyModal} onOpenChange={setShowBuyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-amber-500" />Buy Message Credits
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <Label className="text-xs">Number of Credits</Label>
              <Input
                type="number" min="10" max="10000"
                value={creditAmount}
                onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                data-testid="credit-amount-input"
              />
              <p className="text-xs text-muted-foreground">Min: 10, Max: 10,000 credits</p>
            </div>
            <div className="p-4 bg-secondary/30 rounded-xl space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Credits</span>
                <span>{creditAmount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Price per credit</span>
                <span>₹{pricing.pricePerCredit}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-bold">
                <span>Total Amount</span>
                <span className="text-xl text-primary">₹{(creditAmount * pricing.pricePerCredit).toFixed(2)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBuyModal(false)}>Cancel</Button>
            <Button
              onClick={onClickBuyCredits}
              disabled={buyLoading}
              data-testid="confirm-buy-btn">
              {buyLoading
                ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                : <CreditCard className="h-4 w-4 mr-2" />}
              Pay ₹{(creditAmount * pricing.pricePerCredit).toFixed(2)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Webhook Config Modal ── */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
              <Settings className="h-5 w-5" />Webhook Configuration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
              <p className="text-xs text-blue-500">
                Configure these settings in your Meta WhatsApp Business settings to receive delivery status updates.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Webhook URL</Label>
              <div className="flex gap-2">
                <Input
                  value={configForm.webhookUrl || (window.location.origin + "/api/whatsapp/webhook")}
                  onChange={(e) => setConfigForm({ ...configForm, webhookUrl: e.target.value })}
                  placeholder="https://your-domain.com/api/whatsapp/webhook"
                />
                <Button variant="outline" size="icon"
                  onClick={() => copyToClipboard(configForm.webhookUrl || (window.location.origin + "/api/whatsapp/webhook"))}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Verify Token</Label>
              <div className="flex gap-2">
                <Input value={configForm.webhookVerifyToken} readOnly className="font-mono text-sm" />
                <Button variant="outline" size="icon"
                  onClick={() => copyToClipboard(configForm.webhookVerifyToken)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">Use this token in Meta's webhook configuration</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>Close</Button>
            <Button onClick={onClickSaveConfig}>Save Config</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Test Connection Results Modal ── */}
      <Dialog open={showTestResultModal} onOpenChange={setShowTestResultModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
              {testResults && testResults.success
                ? <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                : <XCircle className="h-5 w-5 text-red-500" />}
              Connection Test Results
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className={"p-4 rounded-xl border " + (
              testResults && testResults.success
                ? "bg-emerald-500/10 border-emerald-500/20"
                : "bg-red-500/10 border-red-500/20"
            )}>
              <p className={"text-sm font-medium " + (
                testResults && testResults.success ? "text-emerald-500" : "text-red-500"
              )}>
                {testResults && testResults.message}
              </p>
            </div>
            {testResults && testResults.results && (
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "tokenValid",       label: "Access Token",  successText: "Valid",                                         failText: "Invalid" },
                  { key: "phoneNumberValid", label: "Phone Number",  successText: testResults.results.phoneNumber || "Verified",  failText: "Not verified" },
                  { key: "wabaValid",        label: "WABA",          successText: testResults.results.wabaName || "Valid",        failText: "Invalid" },
                  { key: "canSendMessages",  label: "Can Send",      successText: "Ready",                                        failText: "Not ready" }
                ].map(function (item) {
                  return (
                    <div key={item.key} className={"p-3 rounded-lg border " + (
                      testResults.results[item.key]
                        ? "bg-emerald-500/10 border-emerald-500/20"
                        : "bg-zinc-500/10 border-zinc-500/20"
                    )}>
                      <div className="flex items-center gap-2 mb-1">
                        {testResults.results[item.key]
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          : <XCircle className="h-4 w-4 text-zinc-400" />}
                        <span className="text-xs font-medium">{item.label}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        {testResults.results[item.key] ? item.successText : item.failText}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
            {testResults && testResults.results &&
              (testResults.results.qualityRating || testResults.results.messagingLimit) && (
              <div className="p-4 bg-secondary/30 rounded-xl space-y-2">
                <p className="text-xs font-medium mb-2">Account Details</p>
                {testResults.results.businessName && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Business Name</span>
                    <span>{testResults.results.businessName}</span>
                  </div>
                )}
                {testResults.results.qualityRating && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Quality Rating</span>
                    <Badge variant="outline" className={"text-[10px] " + (
                      testResults.results.qualityRating === "GREEN" ? "text-emerald-500 border-emerald-500/20" :
                      testResults.results.qualityRating === "YELLOW" ? "text-amber-500 border-amber-500/20" :
                      "text-red-500 border-red-500/20"
                    )}>
                      {testResults.results.qualityRating}
                    </Badge>
                  </div>
                )}
                {testResults.results.messagingLimit && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Messaging Limit</span>
                    <span>{testResults.results.messagingLimit}</span>
                  </div>
                )}
              </div>
            )}
            {testResults && testResults.results &&
              testResults.results.errors && testResults.results.errors.length > 0 && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-xs font-medium text-red-500 mb-2">Errors</p>
                <ul className="space-y-1">
                  {testResults.results.errors.map(function (error, idx) {
                    return (
                      <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                        <XCircle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />{error}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestResultModal(false)}>Close</Button>
            {testResults && !testResults.success && (
              <Button onClick={onClickTestConnection} disabled={testLoading}>
                {testLoading
                  ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  : <RefreshCw className="h-4 w-4 mr-2" />}
                Retry Test
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Send Test Message Modal ── */}
      <Dialog
        open={showSendTestModal}
        onOpenChange={(open) => {
          if (!open) {
            setTestMessageResult(null);
            setTestPhoneNumber("");
          }
          setShowSendTestModal(open);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-500" />Send Test Message
            </DialogTitle>
            <DialogDescription>
              Send a test "Hello World" message to verify your WhatsApp integration is working.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!testMessageResult ? (
              <>
                <div className="space-y-2">
                  <Label className="text-xs flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5" />Mobile Number
                  </Label>
                  <Input
                    type="tel"
                    placeholder="e.g., 9629915678 or +91 9629915678"
                    value={testPhoneNumber}
                    onChange={(e) => setTestPhoneNumber(e.target.value)}
                    data-testid="test-phone-input"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Enter a phone number registered on WhatsApp.
                  </p>
                </div>
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                  <p className="text-xs text-blue-500 font-medium mb-1">What will be sent?</p>
                  <p className="text-xs text-muted-foreground">A "Hello World" test template message will be sent.</p>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                {testMessageResult.success ? (
                  <>
                    <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center">
                      <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                      <p className="text-sm font-medium text-emerald-500 mb-1">Test Message Sent Successfully!</p>
                      <p className="text-xs text-muted-foreground">The message has been delivered to WhatsApp.</p>
                    </div>
                    <div className="p-4 bg-secondary/30 rounded-xl space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Recipient</span>
                        <span>{testMessageResult.phone}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Member</span>
                        <span>{testMessageResult.memberName}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Template</span>
                        <span className="font-mono">{testMessageResult.templateUsed}</span>
                      </div>
                    </div>
                    <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                      <p className="text-xs text-blue-500 font-medium mb-1">What's next?</p>
                      <p className="text-xs text-muted-foreground">Click "Go to Chat" to start a conversation.</p>
                    </div>
                  </>
                ) : (
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-center">
                    <XCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
                    <p className="text-sm font-medium text-red-500 mb-1">Failed to Send Test Message</p>
                    <p className="text-xs text-muted-foreground">{testMessageResult.message}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {!testMessageResult ? (
              <>
                <Button variant="outline" onClick={() => setShowSendTestModal(false)}>Cancel</Button>
                <Button
                  onClick={onClickSendTestMessage}
                  disabled={sendTestLoading || !testPhoneNumber}
                  data-testid="send-test-btn">
                  {sendTestLoading
                    ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    : <Send className="h-4 w-4 mr-2" />}
                  Send Test Message
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setTestMessageResult(null); setTestPhoneNumber(""); }}>
                  Send Another
                </Button>
                {testMessageResult.success && (
                  <Button onClick={handleGoToChat} className="bg-green-600 hover:bg-green-700">
                    <MessageSquare className="h-4 w-4 mr-2" />Go to Chat
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
